import Image from 'next/image';
import ResponsiveImage, { type ResponsiveImageData } from './ResponsiveImage';

/**
 * Feature flag for image optimization strategy
 * Set USE_CLOUDFRONT_DIRECT=true to bypass Vercel optimization (zero cost, custom srcset)
 * Set USE_CLOUDFRONT_DIRECT=false to use Next.js Image (simpler, ~$1-2/month)
 */
const USE_CLOUDFRONT_DIRECT = process.env.NEXT_PUBLIC_USE_CLOUDFRONT_DIRECT === 'true';

interface OptimizedImageProps {
    image: ResponsiveImageData;
    sizes: string;
    className?: string;
    priority?: boolean;
    fill?: boolean;
    fallbackAlt?: string;
    decorative?: boolean;
}

/**
 * Unified image component that switches between CloudFront direct delivery
 * and Next.js Image optimization based on feature flag.
 *
 * @param decorative - If true, forces empty alt text regardless of CMS content.
 *                     Use for purely decorative images (backgrounds, design elements).
 * @param fallbackAlt - Alt text to use when CMS doesn't provide alt text.
 *                      Ignored if decorative is true.
 */
export default function OptimizedImage({
    image,
    sizes,
    className,
    priority = false,
    fill = false,
    fallbackAlt = '',
    decorative = false,
}: OptimizedImageProps) {
    // Decorative images always get empty alt, regardless of CMS content
    const altText = decorative ? '' : image.alt || fallbackAlt;

    if (USE_CLOUDFRONT_DIRECT) {
        // Option A: Direct CloudFront delivery with custom srcset (zero Vercel cost)
        if (fill) {
            // For fill images, use absolute positioning
            return (
                <div className="absolute inset-0">
                    <ResponsiveImage
                        image={image}
                        sizes={sizes}
                        priority={priority}
                        className={className}
                        fallbackAlt={fallbackAlt}
                        decorative={decorative}
                        fill={fill}
                    />
                </div>
            );
        }
        return (
            <ResponsiveImage
                image={image}
                sizes={sizes}
                priority={priority}
                className={className}
                fallbackAlt={fallbackAlt}
                decorative={decorative}
            />
        );
    }

    // Option B: Next.js Image optimization (simpler, better DX, ~$1-2/month)
    // Use the medium size as source for Next.js to optimize from
    if (!image.urlMedium || !image.widthMedium || !image.heightMedium) {
        return null;
    }

    return (
        <Image
            src={image.urlMedium}
            width={fill ? undefined : image.widthMedium}
            height={fill ? undefined : image.heightMedium}
            alt={altText}
            fill={fill}
            sizes={sizes}
            priority={priority}
            className={className}
            unoptimized={false} // Let Next.js optimize
        />
    );
}
